import classNames from 'classnames';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from 'atoms/layout/flex';
import { DisplayS, LabelL } from 'atoms/typography';
import { Img } from 'atoms/ui/Img';
import { laptopAndAbove, tabletAndAbove } from 'style/mediaQuery';

import { Pictures } from '../types';

const Root = styled(Vertical)`
  flex-direction: column-reverse;
  justify-content: flex-end;
  @media ${tabletAndAbove} {
    flex-direction: column;
  }
`;

const StepContainer = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  align-items: flex-start;
  justify-content: flex-end;
  height: 100%;
`;

const Illu = styled.picture`
  display: block;
  max-width: 100%;
  height: var(--blockHeight);
  object-fit: contain;

  &.lastStep {
    align-self: flex-end;
  }
`;

const TitleBox = styled(Vertical).attrs({ gap: 0 })`
  position: absolute;
  align-self: flex-start;
  flex-direction: column;
  justify-content: flex-end;
  line-height: 0.5;
  word-wrap: break-word;
  word-break: break-word;
  width: calc(30 * var(--unit));
  @media ${laptopAndAbove} {
    line-height: 0.8;
    width: calc(40 * var(--unit));
  }
`;

const Title = styled(DisplayS)`
  font-family: var(--rigid-square);
  text-transform: uppercase;
  color: var(--c-white);
  max-width: 100%;
  overflow: hidden;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  @media ${laptopAndAbove} {
    font: var(--t-display-l);
    font-family: var(--rigid-square);
  }
`;

const StepNumber = styled.div`
  font-family: var(--rigid-square);
  font-size: calc(15 * var(--unit));
  color: var(--c-nd-200-opaque);
  line-height: 1;
  flex-shrink: 0;
  @media ${laptopAndAbove} {
    font-size: calc(20 * var(--unit));
  }
`;

type StepByStepProps = {
  img: Pictures & {
    height: number;
    width: number;
  };
  number: number;
  title: ReactNode;
  subtitle: ReactNode;
  isLastIllu: boolean;
};

export const StepByStep = ({
  img,
  number,
  title,
  subtitle,
  isLastIllu,
}: StepByStepProps) => {
  return (
    <Root>
      <Illu className={classNames({ lastStep: isLastIllu })}>
        <source srcSet={img.avif} type="image/avif" />
        <Img
          src={img.pictureUrl}
          fetchpriority="low"
          height={img.height}
          width={img.width}
        />
      </Illu>
      <StepContainer>
        <StepNumber>{number}</StepNumber>
        <TitleBox>
          <Title>{title}</Title>
          <LabelL>{subtitle}</LabelL>
        </TitleBox>
      </StepContainer>
    </Root>
  );
};
